import React, { useState } from 'react';
import { ChevronDownIcon, ClipboardCheckIcon, TruckIcon } from 'lucide-react';
interface AccordionItem {
  id: string;
  title: string;
  content: string;
}
export function SpecializedServices() {
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const toggleAccordion = (id: string) => {
    setOpenAccordion(openAccordion === id ? null : id);
  };
  const dotCdlItems: AccordionItem[] = [{
    id: 'dot-inspections',
    title: 'DOT Inspections',
    content: `<p class="mb-4">Garrett Auto and Truck Service offers DOT inspections for all types of commercial vehicles. Our certified technicians ensure your vehicle meets all Department of Transportation safety standards.</p>
      <p class="mb-4">A DOT inspection is a thorough examination of your commercial vehicle to ensure it meets federal safety regulations. Regular inspections help prevent roadside violations and keep your fleet compliant.</p>
      <div class="bg-black text-white p-6 rounded border-l-4 border-[#B1050B]">
        <h4 class="font-bold text-lg mb-3 text-[#B1050B]">What We Inspect:</h4>
        <div class="grid md:grid-cols-2 gap-4">
          <ul class="space-y-2 text-gray-300">
            <li>• Brake systems</li>
            <li>• Lighting and electrical systems</li>
            <li>• Steering mechanisms</li>
            <li>• Suspension components</li>
          </ul>
          <ul class="space-y-2 text-gray-300">
            <li>• Tires and wheels</li>
            <li>• Exhaust systems</li>
            <li>• Coupling devices</li>
            <li>• Emergency equipment</li>
          </ul>
        </div>
      </div>`
  }, {
    id: 'cdl-training',
    title: 'CDL Training Vehicle Inspections',
    content: `<p class="mb-4">We provide specialized inspection services for CDL training vehicles, ensuring they meet all requirements for driver testing and certification programs.</p>
      <p class="mb-4">Training vehicles must meet strict safety standards to be used for CDL testing. Our comprehensive inspections ensure your training fleet is always test-ready and compliant with state and federal regulations.</p>
      <div class="bg-black text-white p-4 rounded border-l-4 border-[#B1050B]">
        <p class="text-gray-300"><strong class="text-[#B1050B]">For CDL Training Schools:</strong> We understand the importance of keeping your training vehicles in top condition. Our fast turnaround times and thorough inspections help minimize downtime and keep your students on schedule.</p>
      </div>`
  }, {
    id: 'annual-dot',
    title: 'Annual DOT Inspections',
    content: `<p class="mb-4">Federal regulations require commercial vehicles to undergo annual inspections. Stay compliant and avoid penalties with our thorough annual DOT inspection service.</p>
      <p class="mb-4">Annual inspections are mandatory for commercial motor vehicles. Our certified inspectors provide detailed documentation and can address any issues found during the inspection to get you back on the road quickly.</p>
      <div class="bg-black text-white p-6 rounded border-l-4 border-[#B1050B]">
        <h4 class="font-bold text-lg mb-3 text-[#B1050B]">Why Choose Us for Annual Inspections:</h4>
        <ul class="space-y-2 text-gray-300">
          <li>• Certified DOT inspectors</li>
          <li>• Comprehensive inspection reports</li>
          <li>• Same-day service available</li>
          <li>• On-site repair capabilities</li>
          <li>• Fleet inspection scheduling</li>
          <li>• Competitive pricing</li>
        </ul>
      </div>`
  }];
  const trailerRvItems: AccordionItem[] = [{
    id: 'motor-home',
    title: 'Motor Home Repair',
    content: `<p class="mb-4">Expert motor home repair services to keep your RV road-ready. From routine maintenance to major repairs, we have the expertise to handle all your motor home needs.</p>
      <p class="mb-4">Our technicians are experienced in diagnosing and repairing motor home systems including engines, transmissions, brakes, electrical systems, and more.</p>
      <a href="https://www.motorcitytrailerrepair.com/" target="_blank" rel="noopener noreferrer" class="inline-flex items-center text-[#B1050B] hover:text-[#8A0408] font-medium transition-colors">
        Visit Motor City Trailer Repair →
      </a>`
  }, {
    id: 'trailer-repair',
    title: 'Trailer Repair',
    content: `<p class="mb-4">Comprehensive trailer repair services for all types of trailers. We handle everything from brake repairs to structural work, ensuring your trailer is safe and reliable.</p>
      <p class="mb-4">Our trailer repair services include brake systems, lighting, axles, suspension, flooring, and structural repairs for utility, cargo, and commercial trailers.</p>
      <a href="https://www.motorcitytrailerrepair.com/" target="_blank" rel="noopener noreferrer" class="inline-flex items-center text-[#B1050B] hover:text-[#8A0408] font-medium transition-colors">
        Visit Motor City Trailer Repair →
      </a>`
  }, {
    id: 'specialized-trailers',
    title: 'Specialized Trailer Services',
    content: `<p class="mb-4">We provide expert repair and maintenance for specialized trailers including horse trailers, farm equipment trailers, travel trailers, utility trailers, and enclosed trailers.</p>
      <ul class="space-y-2 text-gray-700 mb-4">
        <li>• Horse trailers</li>
        <li>• Farm equipment trailers</li>
        <li>• Travel trailers</li>
        <li>• Utility trailers</li>
        <li>• Enclosed trailers</li>
      </ul>
      <a href="https://www.motorcitytrailerrepair.com/" target="_blank" rel="noopener noreferrer" class="inline-flex items-center text-[#B1050B] hover:text-[#8A0408] font-medium transition-colors">
        Visit Motor City Trailer Repair →
      </a>`
  }, {
    id: 'equipment-repainting',
    title: 'Equipment Repainting',
    content: `<p class="mb-4">Professional equipment repainting services to restore and protect your vehicles and equipment. Quality paint work that stands up to the elements and heavy use.</p>
      <p class="mb-4">We provide complete paint and refinishing services for trucks, trailers, equipment, and commercial vehicles with durable coatings designed for commercial applications.</p>
      <a href="https://www.motorcitytruckcollision.com/" target="_blank" rel="noopener noreferrer" class="inline-flex items-center text-[#B1050B] hover:text-[#8A0408] font-medium transition-colors">
        Visit Motor City Truck Collision →
      </a>`
  }];
  return <section id="specialized-services" className="py-20 bg-gray-50" data-id="element-8529">
      <div className="container mx-auto px-4" data-id="element-8530">
        <div className="max-w-6xl mx-auto" data-id="element-8531">
          <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center" data-id="element-8532">
            Specialized Services
          </h2>
          <p className="text-xl text-gray-600 text-center mb-16 max-w-3xl mx-auto" data-id="element-8533">
            DOT inspections, CDL vehicle certification, trailer repair, RV
            maintenance, and equipment services for commercial and personal
            vehicles.
          </p>

          <div className="grid lg:grid-cols-2 gap-8" data-id="element-8534">
            {/* DOT & CDL Services */}
            <div className="bg-white rounded-lg shadow-lg border-2 border-black overflow-hidden" data-id="element-8535">
              {/* Header Image */}
              <div className="relative h-48 overflow-hidden" data-id="element-8536">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3Yegs2t4f2Nf99CRYDKvZu/DOT-CDL-Services.png" alt="DOT inspection facility with diagnostic equipment" className="w-full h-full object-cover" data-id="element-8537" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-8538"></div>
              </div>

              <div className="p-8" data-id="element-8539">
                <div className="flex items-center mb-4" data-id="element-8540">
                  <div className="bg-[#B1050B] p-3 rounded-lg mr-4" data-id="element-8541">
                    <ClipboardCheckIcon size={32} className="text-white" data-id="element-8542" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900" data-id="element-8543">
                    DOT & CDL Services
                  </h3>
                </div>
                <p className="text-gray-700 mb-6" data-id="element-8544">
                  Certified DOT inspections and CDL training vehicle
                  certifications for commercial fleets. Our experienced
                  inspectors ensure your vehicles meet all federal safety
                  regulations and compliance requirements, helping you avoid
                  roadside violations and keep your fleet operating legally.
                </p>

                <div className="space-y-3" data-id="element-8545">
                  {dotCdlItems.map(item => <div key={item.id} className="border-2 border-gray-300 bg-gray-50" data-id="element-8546">
                      <button onClick={() => toggleAccordion(item.id)} className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-100 transition-colors" data-id="element-8547">
                        <h4 className="font-bold text-gray-900 pr-4" data-id="element-8548">
                          {item.title}
                        </h4>
                        <ChevronDownIcon size={20} className={`text-[#B1050B] flex-shrink-0 transition-transform duration-200 ${openAccordion === item.id ? 'transform rotate-180' : ''}`} data-id="element-8549" />
                      </button>

                      {openAccordion === item.id && <div className="px-4 pb-4 border-t-2 border-gray-200" data-id="element-8550">
                          <div className="pt-4 text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{
                      __html: item.content
                    }} data-id="element-8551" />
                        </div>}
                    </div>)}
                </div>
              </div>
            </div>

            {/* Trailer & RV Services */}
            <div className="bg-white rounded-lg shadow-lg border-2 border-black overflow-hidden" data-id="element-8552">
              {/* Header Image */}
              <div className="relative h-48 overflow-hidden" data-id="element-8553">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/91EcYdjrG86K8R4QyQaCsF/Trailer-RV-Services.png" alt="Trailer and RV repair facility with service bays" className="w-full h-full object-cover" data-id="element-8554" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-8555"></div>
              </div>

              <div className="p-8" data-id="element-8556">
                <div className="flex items-center mb-4" data-id="element-8557">
                  <div className="bg-[#B1050B] p-3 rounded-lg mr-4" data-id="element-8558">
                    <TruckIcon size={32} className="text-white" data-id="element-8559" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900" data-id="element-8560">
                    Trailer & RV Services
                  </h3>
                </div>
                <p className="text-gray-700 mb-6" data-id="element-8561">
                  Complete repair and maintenance services for motor homes,
                  travel trailers, utility trailers, and specialized equipment.
                  From brake systems and electrical work to structural repairs
                  and professional repainting, we keep your trailers and RVs
                  safe, reliable, and road-ready for work or recreation.
                </p>

                <div className="space-y-3" data-id="element-8562">
                  {trailerRvItems.map(item => <div key={item.id} className="border-2 border-gray-300 bg-gray-50" data-id="element-8563">
                      <button onClick={() => toggleAccordion(item.id)} className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-100 transition-colors" data-id="element-8564">
                        <h4 className="font-bold text-gray-900 pr-4" data-id="element-8565">
                          {item.title}
                        </h4>
                        <ChevronDownIcon size={20} className={`text-[#B1050B] flex-shrink-0 transition-transform duration-200 ${openAccordion === item.id ? 'transform rotate-180' : ''}`} data-id="element-8566" />
                      </button>

                      {openAccordion === item.id && <div className="px-4 pb-4 border-t-2 border-gray-200" data-id="element-8567">
                          <div className="pt-4 text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{
                      __html: item.content
                    }} data-id="element-8568" />
                        </div>}
                    </div>)}
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-12 bg-[#B1050B] text-white p-8 rounded-lg text-center border-4 border-black shadow-lg" data-id="element-8569">
            <h3 className="text-2xl font-bold mb-4" data-id="element-8570">
              Schedule Your Service Today
            </h3>
            <p className="text-red-100 mb-6 text-lg" data-id="element-8571">
              Contact us for DOT inspections, trailer repairs, RV maintenance,
              or equipment services.
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-black text-white hover:bg-gray-900 px-8 py-3 rounded font-bold transition-colors border-2 border-[#D4AF37]" data-id="element-8572">
              Call (248) 478-8382
            </a>
          </div>
        </div>
      </div>
    </section>;
}